import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { Megaphone, Plus, Pin, Trash2, Edit, X, Save, AlertCircle, Wand2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const CATEGORIES = ['general', 'company_update', 'hr_policy', 'event', 'benefits', 'urgent'];
const CAT_COLORS = { general: '#3b82f6', company_update: '#2E7D32', hr_policy: '#7B1FA2', event: '#F9A825', benefits: '#00897B', urgent: '#dc2626' };

export default function Announcements() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', priority: 'normal', pinned: false });
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'panzix_employee';

  useEffect(() => { loadData(); }, []);
  const loadData = async () => { try { const r = await api.get('/api/announcements'); setAnnouncements(r.data || []); } catch(e){ console.error(e); } finally { setLoading(false); } };

  const handleAIDraft = async () => {
    if (!form.title) { alert('Enter a title first'); return; }
    setDrafting(true);
    try {
      const r = await api.post('/api/chat/send', { message: `Draft a company announcement about: ${form.title}. Category: ${form.category}. Write a professional, engaging announcement suitable for all employees. Use markdown formatting.`, deployed_agent_id: 'ai-draft', session_id: 'draft' });
      // Fallback: use template generation
      const res = await api.post('/api/templates/generate', { template_id: 'employee_handbook_intro', variables: { company_name: form.title, company_mission: form.category, ceo_name: '' } });
      setForm(f => ({ ...f, content: res.data?.content || 'Draft generated.' }));
    } catch (e) {
      // Direct AI draft via email draft endpoint
      try {
        const res = await api.post('/api/email/draft', { to: '', subject: form.title, context: `Company announcement about ${form.title} in category ${form.category}. Write engaging content for all employees.` });
        setForm(f => ({ ...f, content: res.data?.body || 'AI draft generated.' }));
      } catch (e2) { console.error(e2); }
    }
    finally { setDrafting(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.content) return;
    setSaving(true);
    try { await api.post('/api/announcements', form); setShowNew(false); setForm({ title: '', content: '', category: 'general', priority: 'normal', pinned: false }); loadData(); }
    catch(e) { console.error(e); } finally { setSaving(false); }
  };

  const handlePin = async (id, pinned) => { try { await api.patch(`/api/announcements/${id}`, { pinned: !pinned }); loadData(); } catch(e){ console.error(e); } };
  const handleDelete = async (id) => { if (!window.confirm('Delete this announcement?')) return; try { await api.delete(`/api/announcements/${id}`); loadData(); } catch(e){ console.error(e); } };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Announcements</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>Company-wide announcements and important notices.</p>
        </div>
        {isAdmin && <button className="btn-action" onClick={() => setShowNew(true)} data-testid="new-announcement"><Plus size={16} style={{ marginRight: 6 }} />New Announcement</button>}
      </div>

      {announcements.length === 0 ? (
        <div className="empty-state"><Megaphone size={64} /><h4>No Announcements</h4><p>No announcements have been posted yet.</p></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {announcements.map(ann => (
            <div key={ann.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid={`ann-${ann.id}`}>
              <div style={{ height: 4, background: CAT_COLORS[ann.category] || '#3b82f6' }} />
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  {ann.pinned && <Pin size={14} color="#F9A825" fill="#F9A825" />}
                  {ann.priority === 'urgent' && <AlertCircle size={14} color="#dc2626" />}
                  <h4 style={{ fontSize: 18, fontWeight: 700, flex: 1 }}>{ann.title}</h4>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${CAT_COLORS[ann.category] || '#3b82f6'}18`, color: CAT_COLORS[ann.category] || '#3b82f6', textTransform: 'capitalize' }}>{ann.category.replace('_', ' ')}</span>
                </div>
                <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 12 }}><ReactMarkdown>{ann.content}</ReactMarkdown></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Posted by {ann.author_name} on {new Date(ann.created_at).toLocaleDateString()}</span>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handlePin(ann.id, ann.pinned)} className="btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }}><Pin size={12} style={{ marginRight: 3 }} />{ann.pinned ? 'Unpin' : 'Pin'}</button>
                      <button onClick={() => handleDelete(ann.id)} className="btn-delete" style={{ padding: '4px 10px', fontSize: 11 }}><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="task-modal slide-up" style={{ maxWidth: 580 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>New Announcement</h3>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Announcement title" data-testid="ann-title" /></div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}><label>Category</label>
                <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}><label>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                  <option value="normal">Normal</option><option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="form-group"><label>Content (Markdown supported)</label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <button onClick={handleAIDraft} disabled={drafting || !form.title} className="btn-secondary" style={{ fontSize: 11, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 4 }} data-testid="ai-draft-btn">
                  <Wand2 size={12} />{drafting ? 'Drafting...' : 'AI Draft'}
                </button>
              </div>
              <textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} placeholder="Write your announcement... Markdown formatting is supported." style={{ width: '100%', minHeight: 150, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} data-testid="ann-content" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({...f, pinned: e.target.checked}))} /> Pin this announcement to the top
            </label>
            <button className="btn-action" onClick={handleSave} disabled={saving || !form.title || !form.content} style={{ width: '100%' }} data-testid="post-announcement">
              <Megaphone size={14} style={{ marginRight: 6 }} />{saving ? 'Posting...' : 'Post Announcement'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
